import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { presetAPI } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator
} from '@/components/ui/dropdown-menu';
import {
  Loader2, Sparkles, Target, Mic2, Plus, Globe, Lock, MoreVertical,
  Pencil, Trash2, Eye, EyeOff, Bot, User as UserIcon
} from 'lucide-react';
import { toast } from 'sonner';

export default function PresetsPage() {
  const [presets, setPresets] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => { loadPresets(); }, []);

  const loadPresets = async () => {
    try {
      const res = await presetAPI.list();
      setPresets(res.data);
    } catch (err) {
      toast.error('Failed to load presets');
    } finally {
      setLoading(false);
    }
  };

  const handlePublish = async (id) => {
    try {
      await presetAPI.publish(id);
      toast.success('Preset published');
      loadPresets();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to publish');
    }
  };

  const handleUnpublish = async (id) => {
    try {
      await presetAPI.unpublish(id);
      toast.success('Preset unpublished');
      loadPresets();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to unpublish');
    }
  };

  const handleDelete = async (id) => {
    try {
      await presetAPI.delete(id);
      toast.success('Preset deleted');
      loadPresets();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const systemPresets = presets.filter(p => p.is_system);
  const myDrafts = presets.filter(p => p.created_by === user?.id && p.status === 'draft');
  const myPublished = presets.filter(p => p.created_by === user?.id && p.status === 'published');
  const communityPresets = presets.filter(p => !p.is_system && p.status === 'published' && p.created_by !== user?.id);

  return (
    <div className="p-8 space-y-8" data-testid="presets-page">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Preset Library</h1>
          <p className="text-muted-foreground mt-2">Content generation presets tailored for specific platforms, audiences, and content verticals.</p>
        </div>
        <Button
          onClick={() => navigate('/presets/builder')}
          className="rounded-full bg-primary hover:bg-primary/90 shadow-lg shadow-indigo-500/25 transition-all duration-300 hover:scale-[1.02] active:scale-[0.98]"
          data-testid="create-preset-button"
        >
          <Plus className="w-4 h-4 mr-2" /> Create Preset
        </Button>
      </div>

      {/* My Drafts */}
      {myDrafts.length > 0 && (
        <div className="space-y-4">
          <h2 className="font-heading text-lg font-semibold flex items-center gap-2">
            <Lock className="w-4 h-4 text-yellow-400" /> My Drafts ({myDrafts.length})
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {myDrafts.map((preset) => (
              <PresetCard key={preset.id} preset={preset} isOwner onPublish={handlePublish} onUnpublish={handleUnpublish} onDelete={handleDelete} navigate={navigate} />
            ))}
          </div>
        </div>
      )}

      {/* My Published */}
      {myPublished.length > 0 && (
        <div className="space-y-4">
          <h2 className="font-heading text-lg font-semibold flex items-center gap-2">
            <Globe className="w-4 h-4 text-emerald-400" /> My Published ({myPublished.length})
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {myPublished.map((preset) => (
              <PresetCard key={preset.id} preset={preset} isOwner onPublish={handlePublish} onUnpublish={handleUnpublish} onDelete={handleDelete} navigate={navigate} />
            ))}
          </div>
        </div>
      )}

      {/* Community Preset Library */}
      <div className="space-y-4">
        <h2 className="font-heading text-lg font-semibold flex items-center gap-2">
          <Globe className="w-4 h-4 text-cyan-400" /> Community Preset Library
          {communityPresets.length > 0 && (
            <Badge variant="secondary" className="text-[10px] ml-1">{communityPresets.length}</Badge>
          )}
        </h2>
        {communityPresets.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {communityPresets.map((preset) => (
              <PresetCard key={preset.id} preset={preset} navigate={navigate} />
            ))}
          </div>
        ) : (
          <Card className="bg-zinc-900/40 border-white/5 border-dashed">
            <CardContent className="py-8 text-center">
              <UserIcon className="w-8 h-8 text-muted-foreground/20 mx-auto mb-2" strokeWidth={1.5} />
              <p className="text-sm text-muted-foreground">No community presets yet</p>
              <p className="text-xs text-muted-foreground/60 mt-1">Be the first to publish a preset!</p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* System Presets */}
      {systemPresets.length > 0 && (
        <div className="space-y-4">
          <h2 className="font-heading text-lg font-semibold flex items-center gap-2">
            <Bot className="w-4 h-4 text-primary" /> System Presets ({systemPresets.length})
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {systemPresets.map((preset) => (
              <PresetCard key={preset.id} preset={preset} navigate={navigate} />
            ))}
          </div>
        </div>
      )}

      {/* Empty state */}
      {presets.length === 0 && (
        <Card className="bg-zinc-900/60 border-white/5" data-testid="no-presets-state">
          <CardContent className="p-12 text-center">
            <Sparkles className="w-12 h-12 text-muted-foreground/40 mx-auto mb-4" strokeWidth={1.5} />
            <h3 className="font-heading text-xl font-semibold mb-2">No Presets Yet</h3>
            <p className="text-sm text-muted-foreground mb-4">Create your first preset with the AI builder.</p>
            <Button onClick={() => navigate('/presets/builder')} className="rounded-full bg-primary hover:bg-primary/90 shadow-lg shadow-indigo-500/25" data-testid="create-first-preset">
              <Plus className="w-4 h-4 mr-2" /> Create Preset
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function PresetCard({ preset, isOwner, onPublish, onUnpublish, onDelete, navigate }) {
  const isDraft = preset.status === 'draft';
  const isSystem = preset.is_system;

  return (
    <Card
      className="bg-zinc-900/60 border-white/5 hover:border-primary/30 transition-all duration-300 hover:-translate-y-1"
      data-testid={`preset-${preset.id}`}
    >
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500/20 to-purple-500/20 flex items-center justify-center mb-3">
            {isSystem
              ? <Bot className="w-6 h-6 text-primary" strokeWidth={1.5} />
              : <Sparkles className="w-6 h-6 text-primary" strokeWidth={1.5} />}
          </div>
          <div className="flex items-center gap-2">
            {isDraft && (
              <Badge variant="outline" className="text-[10px] bg-yellow-500/10 text-yellow-400 border-yellow-500/20">
                <Lock className="w-2.5 h-2.5 mr-1" /> Draft
              </Badge>
            )}
            {!isDraft && !isSystem && (
              <Badge variant="outline" className="text-[10px] bg-emerald-500/10 text-emerald-400 border-emerald-500/20">
                <Globe className="w-2.5 h-2.5 mr-1" /> Published
              </Badge>
            )}
            {isSystem && (
              <Badge variant="outline" className="text-[10px] bg-indigo-500/10 text-indigo-400 border-indigo-500/20">
                <Bot className="w-2.5 h-2.5 mr-1" /> System
              </Badge>
            )}
            {isOwner && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-7 w-7" data-testid={`preset-menu-${preset.id}`}>
                    <MoreVertical className="w-3.5 h-3.5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-44 bg-zinc-900 border-border/50">
                  {isDraft && (
                    <DropdownMenuItem onClick={() => onPublish(preset.id)} className="cursor-pointer" data-testid={`publish-${preset.id}`}>
                      <Globe className="w-3.5 h-3.5 mr-2 text-emerald-400" /> Publish
                    </DropdownMenuItem>
                  )}
                  {!isDraft && (
                    <DropdownMenuItem onClick={() => onUnpublish(preset.id)} className="cursor-pointer" data-testid={`unpublish-${preset.id}`}>
                      <EyeOff className="w-3.5 h-3.5 mr-2" /> Unpublish
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => onDelete(preset.id)} className="cursor-pointer text-destructive focus:text-destructive" data-testid={`delete-${preset.id}`}>
                    <Trash2 className="w-3.5 h-3.5 mr-2" /> Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
        <CardTitle className="font-heading text-lg">{preset.name}</CardTitle>
        {preset.created_by_name && !isSystem && (
          <p className="text-[10px] text-muted-foreground/60">by {preset.created_by_name}</p>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground leading-relaxed">{preset.description}</p>

        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Target className="w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
            <span className="text-xs text-muted-foreground font-medium">Platforms</span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {preset.targetPlatforms?.map((p, i) => (
              <Badge key={i} variant="secondary" className="text-[10px] bg-indigo-500/10 text-indigo-400 border-0">{p}</Badge>
            ))}
          </div>
        </div>

        <div className="flex items-center gap-4">
          {preset.tone && (
            <div>
              <div className="flex items-center gap-1.5 mb-1">
                <Mic2 className="w-3.5 h-3.5 text-muted-foreground" strokeWidth={1.5} />
                <span className="text-[10px] text-muted-foreground font-medium">Tone</span>
              </div>
              <Badge variant="secondary" className="text-[10px] bg-purple-500/10 text-purple-400 border-0">{preset.tone}</Badge>
            </div>
          )}
          {preset.vertical && (
            <div>
              <span className="text-[10px] text-muted-foreground font-medium block mb-1">Vertical</span>
              <Badge variant="secondary" className="text-[10px] bg-emerald-500/10 text-emerald-400 border-0">{preset.vertical}</Badge>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
